import CloseOutlined from '@icons/CloseOutlined';
import SaveOutlined from '@icons/SaveOutlined';
import SearchOutlined from '@icons/SearchOutlined';
import { Box, CircularProgress, Divider, IconButton, InputAdornment, Tab, TablePagination, Tabs, TextField, Typography } from '@mui/material';
import { useGetSavedViewsQuery } from '@service';
import { SavedView } from '@types';
import React, { useCallback, useMemo, useState } from 'react';
import ShareButtonViewsModalViewRow from './ShareButtonViewsModalViewRow';

export interface ShareButtonViewsModalContentsProps {
  onClose: () => void;
  onSave: () => void;
  onShare: (view: SavedView) => void;
}

const ShareButtonViewsModalContents = ({ onClose, onShare, onSave }: ShareButtonViewsModalContentsProps) => {
  const { data, isFetching } = useGetSavedViewsQuery();
  const [search, setSearch] = useState('');

  const myViews = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    let views = data.filter((view) => view.created_by === view.user_id);
    if (search) {
      views = views.filter((view) => {
        const searchLower = search.toLowerCase();
        if (view.name.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (view.description && view.description.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (view.location && view.location.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (view.state.toLowerCase().includes(searchLower)) {
          return true;
        }

        return false;
      });
    }

    return views;
  }, [data, search]);

  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const onRowsPerPageChanged = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);
  const onPageChanged = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setPage(page);
  }, []);
  const onTabChanged = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  }, []);
  const onCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSearchChanged = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e?.target.value;
    setSearch(value || '');
  }, []);

  const onSaveButtonClicked = useCallback(() => {
    onSave();
  }, [onSave]);

  const renderData = tabIndex === 0 ? myViews : data || [];
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          minHeight: '56px' // pair w/ ShareButtonShareModalContents
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          Views
        </Typography>
        <TextField
          size="small"
          sx={{
            width: '410px'
          }}
          onChange={onSearchChanged}
          value={search}
          label="Search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            )
          }}
        />
        <IconButton onClick={onSaveButtonClicked} size="small">
          <SaveOutlined fontSize="large" />
        </IconButton>

        <IconButton onClick={onCloseModal} size="small">
          <CloseOutlined fontSize="large" />
        </IconButton>
      </Box>
      <Tabs value={tabIndex} onChange={onTabChanged}>
        <Tab label="My Views" />
        <Tab label="All Views" />
      </Tabs>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        {isFetching && (
          <Box sx={{ width: '100%', flex: 1, alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
            <CircularProgress />
          </Box>
        )}
        {renderData.map((view) => (
          <React.Fragment key={view.id}>
            <ShareButtonViewsModalViewRow view={view} onClose={onCloseModal} onShare={onShare} />
            <Divider />
          </React.Fragment>
        ))}
      </Box>
      <TablePagination component="div" count={0} page={page} onPageChange={onPageChanged} rowsPerPage={rowsPerPage} onRowsPerPageChange={onRowsPerPageChanged} />
    </>
  );
};
export default ShareButtonViewsModalContents;
